import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Pre-made key purchase panel for Credits-Pool resellers. They pick a product (account pool)
// and a duration in months (priced by the admin's global per-month grid), then
// buy — the keys are theirs to hand to customers, who redeem them on the public
// /redeem-key page. Also lists the keys this reseller has already bought.
function RedeemBuy({ token, onCreditsChanged, embedded = false }) {
    const [products, setProducts] = useState([]);
    const [stock, setStock] = useState({});
    const [pricing, setPricing] = useState({});
    const [redeemUrl, setRedeemUrl] = useState('');
    const [description, setDescription] = useState('');
    const [productName, setProductName] = useState('');
    const [durationMonths, setDurationMonths] = useState('');
    const [count, setCount] = useState(1);
    const [buying, setBuying] = useState(false);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [lastKeys, setLastKeys] = useState(null);

    const [keys, setKeys] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [filter, setFilter] = useState({ status: 'all', keyword: '' });

    const authFetch = useCallback(async (path, options = {}) => {
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, [token]);

    const loadProducts = useCallback(async () => {
        const { body } = await authFetch('/credits-pool/redeem/products');
        if (body.success) { setProducts(body.products || []); setStock(body.stock || {}); setPricing(body.pricing || {}); setRedeemUrl(body.redeemUrl || ''); setDescription(body.description || ''); }
    }, [authFetch]);

    const loadKeys = useCallback(async () => {
        const q = new URLSearchParams({ page, status: filter.status, keyword: filter.keyword });
        const { body } = await authFetch(`/credits-pool/redeem/my-keys?${q}`);
        if (body.success) { setKeys(body.cards); setPages(body.pagination.totalPages); }
    }, [authFetch, page, filter]);

    useEffect(() => { loadProducts(); }, [loadProducts]);
    useEffect(() => { loadKeys(); }, [loadKeys]);

    // Durations the admin has made available (present in the pricing grid), sorted.
    // Negative = day plan; day plans listed first.
    const months = useMemo(() => Object.keys(pricing).map(Number)
        .filter(n => (n >= 1 && n <= 12) || n === -1 || n === -7 || n === -14)
        .sort((a, b) => (a < 0 && b < 0 ? b - a : a < 0 ? -1 : b < 0 ? 1 : a - b)), [pricing]);
    const durLabel = (m) => (Number(m) < 0 ? `${-Number(m)} day${Number(m) === -1 ? '' : 's'}` : `${m} month${Number(m) > 1 ? 's' : ''}`);
    const selectedProduct = products.find(p => p.name === productName);
    const qty = Math.max(1, parseInt(count, 10) || 0);
    // Free stock that can fill this exact duration = duration-specific + "Any" (0).
    const durStock = durationMonths ? (stock[productName] || {}) : {};
    const availForDuration = durationMonths
        ? Number(durStock[durationMonths] || 0) + Number(durStock[0] || 0)
        : 0;
    const unitPrice = durationMonths
        ? Number(pricing[durationMonths] ?? (Number(durationMonths) === -1 ? 0.1 : Number(durationMonths) === -7 ? 0.4 : Number(durationMonths) === -14 ? 0.6 : durationMonths))
        : 0;
    const totalCost = Math.round(unitPrice * qty * 100) / 100;

    const buy = async () => {
        if (!selectedProduct || !durationMonths || buying) return;
        setBuying(true); setError(null); setNotice(null); setLastKeys(null);
        const { ok, body } = await authFetch('/credits-pool/redeem/buy', {
            method: 'POST',
            body: JSON.stringify({ productName, durationMonths: Number(durationMonths), count: qty }),
        });
        if (ok && body.success) {
            setLastKeys(body.keys);
            setNotice(`Bought ${body.keys.length} key(s) for ${body.creditsSpent} credits. Balance: ${body.creditsRemaining}.`);
            if (onCreditsChanged) onCreditsChanged();
            loadKeys();
        } else {
            setError(body.error || 'Purchase failed');
        }
        setBuying(false);
    };

    const statusBadge = (s) => {
        const map = { active: 'badge-success', unused: 'badge-info', expired: 'badge-warning', banned: 'badge-danger' };
        return <span className={`badge ${map[s] || 'badge-info'}`}>{s}</span>;
    };
    const fmt = (s) => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString() : '—');

    const Section = ({ children }) => (
        embedded ? <section className="redeem-buy-section">{children}</section> : <div className="card">{children}</div>
    );

    useEffect(() => {
        if (!productName && products.length > 0) {
            setProductName(products[0].name);
        }
    }, [productName, products]);

    useEffect(() => {
        if (!durationMonths && months.length > 0) {
            setDurationMonths(String(months[0]));
        }
    }, [durationMonths, months]);

    return (
        <div className={embedded ? 'redeem-buy redeem-buy-embedded' : 'redeem-buy'}>
            <Section>
                <div className="card-header"><div className="card-title">Buy Redemption Keys</div></div>

                {description && (
                    <p className="empty-text" style={{ textAlign: 'left', whiteSpace: 'pre-wrap', marginBottom: 16 }}>{description}</p>
                )}
                {redeemUrl && (
                    <div className="alert alert-info" style={{ marginBottom: 16 }}>
                        <span><Icon name="link" size={16} /></span>
                        <div>Customers redeem their key at <a href={redeemUrl} target="_blank" rel="noreferrer">{redeemUrl}</a> — share this link with your buyers.</div>
                    </div>
                )}

                {products.length === 0 || months.length === 0 ? (
                    <div className="empty-state"><div className="empty-text">No keys are available for purchase yet. Please check back later.</div></div>
                ) : (
                    <>
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Product</label>
                                <select className="form-input" value={productName} onChange={(e) => setProductName(e.target.value)}>
                                    <option value="">-- Select product --</option>
                                    {products.map((p) => (
                                        <option key={p.name} value={p.name}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Duration</label>
                                <select className="form-input" value={durationMonths} onChange={(e) => setDurationMonths(e.target.value)}>
                                    <option value="">-- Select duration --</option>
                                    {months.map((m) => (
                                        <option key={m} value={m}>{durLabel(m)} — {pricing[m] ?? (m === -1 ? 0.1 : m === -7 ? 0.4 : m === -14 ? 0.6 : m)} credits</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Quantity</label>
                                <input className="form-input" type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} />
                            </div>
                        </div>

                        {selectedProduct && durationMonths && (
                            <div className="redeem-buy-summary">
                                <div><span>Unit price</span><strong>{unitPrice} credits</strong></div>
                                <div><span>Duration</span><strong>{durLabel(durationMonths)}</strong></div>
                                <div><span>Quantity</span><strong>{qty}</strong></div>
                                <div className="redeem-buy-total"><span>Total</span><strong>{totalCost} credits</strong></div>
                                <div><span>In stock for this duration</span><strong>{availForDuration}</strong></div>
                                {availForDuration < qty && (
                                    <div className="redeem-buy-stock-warn">
                                        Heads up: only {availForDuration} account(s) in stock for {durLabel(durationMonths)} right now.
                                        Keys still work — customers can redeem once stock is topped up.
                                    </div>
                                )}
                            </div>
                        )}

                        <button className="btn btn-primary" onClick={buy} disabled={!selectedProduct || !durationMonths || buying}>
                            {buying ? 'Buying…' : `Buy${totalCost ? ` for ${totalCost} credits` : ''}`}
                        </button>

                        {notice && <div className="alert alert-success" style={{ marginTop: 16 }}><span><Icon name="check" size={16} /></span><div>{notice}</div></div>}
                        {error && <div className="alert alert-error" style={{ marginTop: 16 }}><span><Icon name="alert" size={16} /></span><div>{error}</div></div>}

                        {lastKeys && (
                            <div style={{ marginTop: 16 }}>
                                <div className="form-label">Your new keys ({lastKeys.length}) — copy and give these to your customers:</div>
                                <textarea className="form-input" readOnly rows={Math.min(8, lastKeys.length)} value={lastKeys.join('\n')} onFocus={(e) => e.target.select()} style={{ fontFamily: 'monospace' }} />
                            </div>
                        )}
                    </>
                )}
            </Section>

            <Section>
                <div className="card-header"><div className="card-title">My Keys</div></div>
                <div className="filters-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    <select className="form-input" style={{ maxWidth: 160 }} value={filter.status} onChange={(e) => { setPage(1); setFilter({ ...filter, status: e.target.value }); }}>
                        {['all', 'unused', 'active', 'expired', 'banned'].map((s) => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
                    </select>
                    <input className="form-input" style={{ maxWidth: 240 }} placeholder="Search key or email…" value={filter.keyword} onChange={(e) => setFilter({ ...filter, keyword: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadKeys(); } }} />
                </div>

                <table className="data-table">
                    <thead><tr><th>Key</th><th>Product</th><th>Duration</th><th>Status</th><th>Bound Email</th><th>Expires</th></tr></thead>
                    <tbody>
                        {keys.length === 0 ? (
                            <tr><td colSpan={6}><div className="empty-state"><div className="empty-text">You haven't bought any keys yet</div></div></td></tr>
                        ) : keys.map((c) => (
                            <tr key={c.key}>
                                <td><code>{c.key}</code></td>
                                <td>{c.productName}</td>
                                <td>{c.durationMonths ? (c.durationMonths < 0 ? `${-c.durationMonths}d` : `${c.durationMonths} mo`) : '—'}</td>
                                <td>{statusBadge(c.status)}</td>
                                <td>{c.accountEmail || '—'}</td>
                                <td>{fmt(c.expireTime)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="pagination" style={{ marginTop: 16 }}>
                    <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                    <span style={{ padding: '0 12px', alignSelf: 'center' }}>Page {page} / {pages}</span>
                    <button className="btn btn-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
                </div>
            </Section>
        </div>
    );
}

export default RedeemBuy;
